import { cn, Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@cortex/ui';
import { roleLabel } from '../roles.ts';
import { Loader2 } from 'lucide-react';
import type * as React from 'react';
import type { Role } from './types.ts';

// Shared bits of the admin pages, with the old pages' classes (so the same markup renders the
// same pixels): the page shell, cards, the error banner and the role badge.

export const PAGE =
  'pt-[30px] pb-24 md:pb-[30px] px-4 md:px-[60px] bg-page-bg min-h-full flex flex-col gap-5';
export const CARD = 'bg-card-bg rounded-[8px] overflow-hidden';
export const CARD_HEAD = 'flex items-center justify-between gap-3 px-5 py-3 border-b border-page-divider';
export const PRIMARY_BTN =
  'flex items-center gap-2 h-9 px-4 rounded-[5px] bg-nav-active text-white text-sm font-medium cursor-pointer hover:bg-nav-active-dark transition-colors disabled:opacity-50 border-0';
export const SMALL_BTN =
  'flex items-center gap-1.5 h-8 px-3 rounded-[5px] border border-page-divider text-xs hover:bg-page-bg bg-transparent text-page-text cursor-pointer disabled:opacity-50';
export const INPUT =
  'w-full h-9 px-3 rounded border border-page-divider bg-page-bg text-page-text outline-none focus:border-nav-active';
export const SELECT = 'h-7 px-2 rounded border border-page-divider bg-page-bg text-xs text-page-text';

export function PageHeader({
  title,
  description,
  children,
}: {
  title: string;
  description?: React.ReactNode;
  children?: React.ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-3">
      <div className="flex flex-col gap-1.5">
        <h1 className="text-[32px] font-medium leading-tight text-page-title">{title}</h1>
        {description && <p className="text-sm font-normal text-page-muted">{description}</p>}
      </div>
      {children && <div className="flex flex-wrap items-center gap-3 mt-1">{children}</div>}
    </div>
  );
}

export function ErrorBanner({ message }: { message: string | null | undefined }) {
  if (!message) return null;
  return (
    <div className="bg-red-50 text-red-800 px-4 py-2 rounded-[5px] text-sm" role="alert">
      {message}
    </div>
  );
}

export function Loading({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="text-page-muted text-sm">
      <Loader2 className="inline animate-spin mr-2" size={16} />
      {label}
    </div>
  );
}

const ROLE_BADGE: Record<string, string> = {
  platform_admin: 'bg-purple-100 text-purple-800',
  org_admin: 'bg-blue-100 text-blue-800',
  member: 'bg-slate-100 text-slate-700',
  app_user: 'bg-amber-100 text-amber-800',
  viewer: 'bg-slate-100 text-slate-700',
  editor: 'bg-blue-100 text-blue-800',
};

export function RoleBadge({ role, className }: { role: Role | string; className?: string }) {
  return (
    <span
      className={cn('text-xs px-2 py-0.5 rounded-full', ROLE_BADGE[role] ?? ROLE_BADGE.member, className)}
    >
      {roleLabel(role)}
    </span>
  );
}

/** The message of an API error body ({ error, message }), or a fallback. */
export function errorMessage(e: unknown, fallback: string): string {
  if (e && typeof e === 'object' && 'message' in e && typeof e.message === 'string') return e.message;
  return fallback;
}

/** Unwraps an openapi-fetch result: the data, or throws the error body. */
export async function unwrap<T>(p: Promise<{ data?: T; error?: unknown; response: Response }>): Promise<T> {
  const r = await p;
  if (r.error !== undefined || !r.response.ok) throw r.error ?? new Error(`HTTP ${r.response.status}`);
  return r.data as T;
}

export const formatDate = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleDateString() : '—';

/** The old ModalShell's look over the @cortex/ui Dialog (focus trap, Escape, overlay click). */
export function Modal({
  title,
  onClose,
  children,
  footer,
  wide = false,
  testId,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  footer?: React.ReactNode;
  wide?: boolean;
  testId?: string;
}) {
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        data-testid={testId}
        className={cn(
          'bg-card-bg border-0 rounded-[8px] p-0 gap-0 shadow-xl text-page-text',
          wide ? 'sm:max-w-xl' : 'sm:max-w-md',
        )}
      >
        <DialogHeader className="px-5 py-3 border-b border-page-divider text-left">
          <DialogTitle className="text-lg font-semibold">{title}</DialogTitle>
          <DialogDescription className="sr-only">{title}</DialogDescription>
        </DialogHeader>
        <div className="px-5 py-4 max-h-[70vh] overflow-y-auto">{children}</div>
        {footer && (
          <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-page-divider">
            {footer}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export const CANCEL_BTN =
  'px-3 py-1.5 rounded text-sm hover:bg-page-bg bg-transparent border-0 text-page-text cursor-pointer';
export const SUBMIT_BTN =
  'px-3 py-1.5 rounded bg-nav-active text-white text-sm font-medium disabled:opacity-50 border-0 cursor-pointer';
export const DANGER_BTN =
  'px-3 py-1.5 rounded bg-red-600 text-white text-sm font-medium disabled:opacity-50 border-0 cursor-pointer';
